import i18next from "i18next";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router";
import BasicInput from "../../components/BasicInput";
import Button from "../../components/Button";
import ConfirmDialog from "../../components/ConfirmDialog";
import Label from "../../components/Label";
import SimpleDialog from "../../components/SimpleDialog";
import * as basinsServices from "../../services/basinsServices";
import { routes, utils } from "../../utils";
import { Basin as BasinType } from "../../utils/types";
import ErrorMessage from "../../components/ErrorMessage";

export default function Basin() {
	const [basin, setBasin] = React.useState<BasinType>({ id: 0, name: "" });

	const [errors, setErrors] = React.useState<any>({});
	const [editing, setEditing] = React.useState<boolean>(false);
	const [deleteModal, setDeleteModal] = React.useState<boolean>(false);

	const { id } = useParams();

	useEffect(() => {
		if (id) {
			basinsServices
				.getBasin(id)
				.then((basin) => {
					setBasin(basin);
				})
				.catch((error) => {});
		}
	}, [id]);

	const navigate = useNavigate();

	const goBack = () => navigate(routes.DASHBOARD);

	const validate = () => {
		const errors: any = {};

		if (!basin.name) {
			errors.name = i18next.t("name-required");
		}

		setErrors(errors);

		return Object.keys(errors).length === 0 ? {} : errors;
	};

	const handelCancel = () => {
		if (editing) {
			setEditing(false);
		} else {
			navigate(-1);
		}
	};

	const handleCreateEditBasin = async () => {
		try {
			const errors = validate();
			if (Object.keys(errors).length !== 0) {
				return;
			}

			if (basin.id) {
				await basinsServices.updateBasin(basin);
			} else {
				await basinsServices.createBasin(basin);
			}

			toast.success(i18next.t(editing ? "basin-edit-success" : "basin-create-success"));
			goBack();
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		}
	};

	const handleDelete = async () => {
		try {
			await basinsServices.deleteBasin(basin.id);
			toast.success(i18next.t("basin-delete-success"));
			navigate(routes.BASINS_LIST);
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
			setDeleteModal(false);
		}
	};

	const showValue = !!id && !editing;

	return (
		<SimpleDialog open={true} onClose={goBack}>
			<ConfirmDialog
				title={i18next.t("delete")}
				handleCancel={() => setDeleteModal(false)}
				handleConfirm={handleDelete}
				subtitle={i18next.t("basin-delete-confirm")}
				open={deleteModal}
			/>

			<div className="d-flex justify-content-between">
				<Label
					isTitle
					text={
						id
							? editing
								? i18next.t("entity-edit", { entity: i18next.t("Basin") })
								: i18next.t("basin-details")
							: i18next.t("entity-create", { entity: i18next.t("Basin") })
					}
				/>
				{editing && (
					<div
						className="text-danger"
						data-testid="delete-button"
						style={{ cursor: "pointer" }}
						onClick={() => setDeleteModal(true)}
					>
						{i18next.t("delete")}
					</div>
				)}
			</div>
			<BasicInput
				label={i18next.t("name")}
				value={basin.name}
				required={true}
				onChange={(e) => setBasin({ ...basin, name: e.target.value })}
				className="mt-2"
				error={errors.full_name}
				datatestid="name-input"
				showValue={showValue}
			/>
			{errors.name && <ErrorMessage error={errors.name} />}

			<div className="d-flex mt-4 justify-content-end">
				<Button
					label={i18next.t(id ? "go-back" : "cancel")}
					onClick={handelCancel}
					className="ms-4"
					secondary
				/>
				{id ? (
					editing ? (
						<Button
							label={i18next.t("save")}
							datatestid="save-button"
							onClick={handleCreateEditBasin}
							className="ms-4"
						/>
					) : (
						<Button
							label={i18next.t("entity-edit", { entity: i18next.t("Basin") })}
							datatestid="edit-button"
							onClick={() => setEditing(true)}
							className="ms-4"
							visible={utils.isAdminOrCollaborator()}
						/>
					)
				) : (
					<Button
						label={i18next.t("entity-create", { entity: i18next.t("Basin") })}
						datatestid="create-button"
						onClick={handleCreateEditBasin}
						className="ms-4"
					/>
				)}
			</div>
		</SimpleDialog>
	);
}
